import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useAuth } from "../../lib/AuthContext";
import {
  ListChecks, Lock, Crown, CircleNotch, Clock, ArrowRight,
  DownloadSimple, Trash, Eye, ArrowClockwise, Plus, X,
  CheckCircle, ShieldWarning, Warning, Users, Pulse,
  ShieldCheck, UploadSimple, FileText, MagnifyingGlass,
  Funnel, CaretUp, CaretDown, ArrowLeft, BookmarkSimple,
  PencilSimple, FloppyDisk,
} from "@phosphor-icons/react";
import { Button } from "../../components/ui/button";
import ToolInfoFooter from "../../components/ToolInfoFooter";
import { BULK_TOOL } from "../../lib/toolCopy";
import { trackEvent } from "../../lib/analytics";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DashboardBulk() {
  const { user, authFetch } = useAuth();
  const tier = user?.tier || "free";
  const canBulk = tier !== "free";

  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeJobId, setActiveJobId] = useState(null);
  const [showNew, setShowNew] = useState(false);

  const loadHistory = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${window.location.origin}/api/bulk-screening/history`);
      if (res.ok) { const d = await res.json(); setJobs(d.jobs || []); }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [authFetch]);

  useEffect(() => { if (canBulk) loadHistory(); else setLoading(false); }, [canBulk, loadHistory]);

  const [rerunning, setRerunning] = useState(false);
  const [recheckDone, setRecheckDone] = useState(null); // null | timestamp string

  const handleRerun = async (jobId) => {
    setRerunning(true);
    setRecheckDone(null);
    try {
      const res = await authFetch(`${window.location.origin}/api/bulk-screening/rerun/${jobId}`, { method: "POST" });
      if (res.ok) {
        const d = await res.json();
        const newJobId = d.job_id;

        // Poll until new job completes (min 2s overlay)
        const minDelay = new Promise(r => setTimeout(r, 2000));
        const pollDone = new Promise((resolve) => {
          const interval = setInterval(async () => {
            try {
              const sr = await fetch(`${window.location.origin}/api/bulk-screening/status/${newJobId}`);
              if (sr.ok) {
                const sd = await sr.json();
                if (sd.status === "complete") { clearInterval(interval); resolve(); }
              }
            } catch { /* keep polling */ }
          }, 1500);
        });
        await Promise.all([minDelay, pollDone]);

        // Show "completed" for 1.5s
        setRecheckDone(new Date().toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }));
        await new Promise(r => setTimeout(r, 1500));

        setActiveJobId(newJobId);
        loadHistory();
      }
    } catch (e) { console.error(e); }
    finally { setRerunning(false); setRecheckDone(null); }
  };

  const handleDelete = async (jobId) => {
    try {
      await authFetch(`${window.location.origin}/api/bulk-screening/jobs/${jobId}`, { method: "DELETE" });
      setJobs(prev => prev.filter(j => j.job_id !== jobId));
      if (activeJobId === jobId) setActiveJobId(null);
    } catch (e) { console.error(e); }
  };

  if (!canBulk) {
    return (
      <>
        <Helmet><title>Bulk Lookup | NPI Lookup</title></Helmet>
        <div data-testid="dashboard-bulk">
          <h1 className="font-display font-bold text-2xl text-slate-900 mb-6">Bulk Lookup</h1>
          <div className="bg-white rounded-xl border-2 border-slate-200 p-12 text-center">
            <Lock className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h2 className="font-display font-bold text-xl text-slate-900 mb-2">Bulk Lookup requires a Pro plan</h2>
            <p className="text-sm text-slate-500 max-w-md mx-auto mb-6">Screen up to 100 NPIs (Pro) or 5,000 NPIs (Team/Enterprise) with saved history and re-check.</p>
            <Link to="/pricing"><Button className="bg-teal hover:bg-teal/90 text-white rounded-xl gap-2 h-11 px-6"><Crown className="w-4 h-4" weight="fill" /> Upgrade to Pro</Button></Link>
          </div>
        </div>
      </>
    );
  }

  if (activeJobId) {
    return (
      <>
        {rerunning && <RecheckOverlay done={recheckDone} />}
        <JobResultsViewer jobId={activeJobId} onBack={() => { setActiveJobId(null); loadHistory(); }} authFetch={authFetch} onRerun={handleRerun} rerunning={rerunning} />
      </>
    );
  }

  const savedJobs = jobs.filter(j => j.saved);
  const unsavedJobs = jobs.filter(j => !j.saved);

  return (
    <>
      <Helmet><title>Bulk Lookup | NPI Lookup</title></Helmet>
      <div data-testid="dashboard-bulk">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-display font-bold text-2xl text-slate-900">Bulk Lookup</h1>
            <p className="text-sm text-slate-500">{BULK_TOOL.tagline}</p>
          </div>
          <Button onClick={() => setShowNew(!showNew)} className="bg-teal hover:bg-teal/90 text-white rounded-lg gap-2 text-sm" data-testid="new-screening-btn">
            <Plus className="w-4 h-4" /> New Screening
          </Button>
        </div>

        {showNew && <QuickScreening authFetch={authFetch} onComplete={(jid) => { setShowNew(false); setActiveJobId(jid); loadHistory(); }} onClose={() => setShowNew(false)} />}

        {loading ? (
          <div className="p-12 text-center"><CircleNotch className="w-6 h-6 text-teal animate-spin mx-auto" /></div>
        ) : jobs.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
            <ListChecks className="w-10 h-10 text-slate-300 mx-auto mb-3" weight="fill" />
            <h3 className="font-display font-semibold text-slate-900 mb-1">No screening history</h3>
            <p className="text-sm text-slate-500 mb-4">Run your first bulk screening — results are saved for 30 days.</p>
            <Button onClick={() => setShowNew(true)} className="bg-teal hover:bg-teal/90 text-white rounded-lg gap-2"><Plus className="w-4 h-4" /> Run First Screening</Button>
          </div>
        ) : (
          <>
            {/* Saved searches */}
            {savedJobs.length > 0 && (
              <div className="mb-6">
                <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-2">
                  <BookmarkSimple className="w-4 h-4" weight="fill" /> Saved Screenings
                </h2>
                <div className="space-y-2">
                  {savedJobs.map(job => <JobCard key={job.job_id} job={job} onView={setActiveJobId} onRerun={handleRerun} onDelete={handleDelete} rerunning={rerunning} />)}
                </div>
              </div>
            )}

            {/* Recent (unsaved) */}
            <div>
              <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-2">
                <Clock className="w-4 h-4" /> Recent History
                <span className="text-xs font-normal text-slate-400 normal-case">— auto-deleted after 30 days</span>
              </h2>
              {unsavedJobs.length === 0 ? (
                <p className="text-sm text-slate-400 py-4">No unsaved screenings. Saved screenings are kept for 30 days.</p>
              ) : (
                <div className="space-y-2">
                  {unsavedJobs.map(job => <JobCard key={job.job_id} job={job} onView={setActiveJobId} onRerun={handleRerun} onDelete={handleDelete} rerunning={rerunning} />)}
                </div>
              )}
            </div>
          </>
        )}

        <ToolInfoFooter title={BULK_TOOL.title} steps={BULK_TOOL.steps} faqs={BULK_TOOL.faqs} />
      </div>
    </>
  );
}

function JobCard({ job, onView, onRerun, onDelete, rerunning }) {
  const title = job.title || `Bulk Screening — ${new Date(job.created_at).toLocaleDateString()}`;
  return (
    <div className={`bg-white rounded-xl border border-slate-200 hover:border-slate-300 transition-all ${rerunning ? "opacity-60 pointer-events-none" : ""}`}>
      <div className="flex items-center justify-between p-4">
        <button onClick={() => job.status === "complete" && onView(job.job_id)} className="flex items-center gap-3 text-left flex-1 min-w-0">
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
            job.status === "complete" ? "bg-emerald-50" : "bg-amber-50"
          }`}>
            {job.status === "complete" ? <CheckCircle className="w-4.5 h-4.5 text-emerald-500" weight="fill" /> : <CircleNotch className="w-4.5 h-4.5 text-amber-500 animate-spin" />}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <p className="font-medium text-slate-900 truncate">{title}</p>
              {job.saved && <BookmarkSimple className="w-3.5 h-3.5 text-teal shrink-0" weight="fill" />}
            </div>
            <div className="flex items-center gap-3 text-xs text-slate-400 mt-0.5">
              <span>{job.total} NPI{job.total !== 1 ? "s" : ""}</span>
              <span>{new Date(job.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
              {job.summary && (
                <>
                  <span className="text-emerald-600">{job.summary.active} active</span>
                  {job.summary.high_risk > 0 && <span className="text-red-500">{job.summary.high_risk} flagged</span>}
                </>
              )}
            </div>
          </div>
        </button>
        <div className="flex items-center gap-0.5 shrink-0">
          {job.status === "complete" && (
            <>
              <button onClick={() => onView(job.job_id)} className="p-2 text-slate-400 hover:text-teal rounded-lg hover:bg-slate-50" title="View Results"><Eye className="w-4 h-4" /></button>
              <button onClick={() => onRerun(job.job_id)} className="p-2 text-slate-400 hover:text-navy rounded-lg hover:bg-slate-50" title="Re-check">
                        <ArrowClockwise className="w-4 h-4" /></button>
              <a href={`${API_URL}/api/bulk-screening/download/${job.job_id}`} target="_blank" rel="noreferrer" className="p-2 text-slate-400 hover:text-teal rounded-lg hover:bg-slate-50" title="Download"><DownloadSimple className="w-4 h-4" /></a>
            </>
          )}
          <button onClick={() => onDelete(job.job_id)} className="p-2 text-slate-400 hover:text-red-500 rounded-lg hover:bg-red-50" title="Delete"><Trash className="w-4 h-4" /></button>
        </div>
      </div>
    </div>
  );
}

function QuickScreening({ authFetch, onComplete, onClose }) {
  const [mode, setMode] = useState("paste");
  const [text, setText] = useState("");
  const [title, setTitle] = useState("");
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const fileRef = useRef(null);

  const npis = text.trim() ? text.split(/[\n,\s]+/).filter(t => t.trim().replace(/\D/g, "").length === 10) : [];

  const handleSubmit = async () => {
    setSubmitting(true); setError(null);
    try {
      let res;
      if (mode === "paste") {
        res = await authFetch(`${window.location.origin}/api/bulk-screening/submit-text`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ text, title: title.trim() }),
        });
      } else {
        const fd = new FormData(); fd.append("file", file);
        res = await authFetch(`${window.location.origin}/api/bulk-screening/upload`, { method: "POST", body: fd });
      }
      const d = await res.json();
      if (!res.ok) { setError(d.detail || "Failed"); setSubmitting(false); return; }
      trackEvent("bulk_screening_create", { source: mode, npi_count: d.total || 0 });
      onComplete(d.job_id);
    } catch { setError("Failed to submit"); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display font-semibold text-base text-slate-900">New Screening</h3>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
      </div>

      {/* Title */}
      <div className="mb-3">
        <label className="block text-xs font-semibold text-slate-500 mb-1">Name this screening (optional — helps find it later)</label>
        <input type="text" value={title} onChange={e => setTitle(e.target.value)}
          placeholder="e.g. Texas Providers, Q2 Contractors, Westside Medical..."
          className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal/30" />
      </div>

      {/* Mode tabs */}
      <div className="flex items-center bg-slate-100 rounded-lg p-0.5 w-fit mb-3">
        <button onClick={() => setMode("paste")} className={`px-3 py-1.5 text-xs font-medium rounded-md ${mode === "paste" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}>Paste NPIs</button>
        <button onClick={() => setMode("upload")} className={`px-3 py-1.5 text-xs font-medium rounded-md ${mode === "upload" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}>Upload CSV</button>
      </div>

      {mode === "paste" ? (
        <div className="mb-3">
          <textarea value={text} onChange={e => setText(e.target.value)} placeholder="Paste NPI numbers — one per line, comma-separated, or space-separated..." rows={4}
            className="w-full text-sm font-mono border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal/30 bg-slate-50" />
          {npis.length > 0 && <p className="text-xs text-teal font-medium mt-1">{npis.length} valid NPIs</p>}
        </div>
      ) : (
        <div className="mb-3">
          <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={e => setFile(e.target.files?.[0] || null)} />
          {file ? (
            <div className="flex items-center gap-2 text-sm py-2"><FileText className="w-4 h-4 text-teal" weight="fill" /><span className="text-slate-700">{file.name}</span>
              <button onClick={() => setFile(null)} className="text-red-500 text-xs ml-2">Remove</button></div>
          ) : (
            <button onClick={() => fileRef.current?.click()} className="w-full border-2 border-dashed border-slate-200 rounded-lg p-4 text-center text-sm text-slate-500 hover:border-teal/50">
              <UploadSimple className="w-5 h-5 mx-auto mb-1" /> Click to select CSV
            </button>
          )}
        </div>
      )}

      {error && <p className="text-xs text-red-500 mb-2">{error}</p>}
      <Button onClick={handleSubmit} disabled={submitting || (mode === "paste" && npis.length === 0) || (mode === "upload" && !file)}
        className="bg-teal hover:bg-teal/90 text-white rounded-lg gap-2 text-sm w-full">
        {submitting ? <CircleNotch className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" weight="fill" />}
        {title.trim() ? `Screen "${title.trim()}"` : `Screen${mode === "paste" && npis.length > 0 ? ` ${npis.length} NPIs` : ""}`}
      </Button>
    </div>
  );
}

function JobResultsViewer({ jobId, onBack, authFetch, onRerun, rerunning }) {
  const [results, setResults] = useState([]);
  const [summary, setSummary] = useState(null);
  const [jobMeta, setJobMeta] = useState(null);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [title, setTitle] = useState("");
  const [saved, setSaved] = useState(false);
  const [isRerun, setIsRerun] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [riskFilter, setRiskFilter] = useState("all");
  const [pecosFilter, setPecosFilter] = useState("all");
  const [sortBy, setSortBy] = useState("npi");
  const [sortDir, setSortDir] = useState("asc");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [showFilters, setShowFilters] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [saving, setSaving] = useState(false);
  const [reviewRow, setReviewRow] = useState(null);
  const pollRef = useRef(null);

  const fetchResults = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        page, per_page: perPage, search, status_filter: statusFilter,
        pecos_filter: pecosFilter, risk_filter: riskFilter,
        sort_by: sortBy, sort_dir: sortDir,
      });
      const res = await fetch(`${API_URL}/api/bulk-screening/results/${jobId}?${params}`);
      if (res.ok) {
        const d = await res.json();
        setResults(d.results); setSummary(d.summary); setTotal(d.total); setTotalPages(d.total_pages);
        setJobMeta({ status: d.job_status, processed: d.processed, total: d.job_total });
        setTitle(d.title || ""); setSaved(d.saved || false);
        setIsRerun(Boolean(d.is_rerun));
        if (d.job_status === "complete" && pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      }
    } catch (e) { console.error(e); }
  }, [jobId, page, perPage, search, statusFilter, pecosFilter, riskFilter, sortBy, sortDir]);

  useEffect(() => {
    fetchResults();
    pollRef.current = setInterval(fetchResults, 2500);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  useEffect(() => { if (jobMeta?.status === "complete") fetchResults(); }, [search, statusFilter, pecosFilter, riskFilter, sortBy, sortDir, page, perPage]);

  const handleSave = async () => {
    const t = newTitle.trim() || title || "Untitled";
    setSaving(true);
    try {
      const res = await authFetch(`${window.location.origin}/api/bulk-screening/jobs/${jobId}/save`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title: t }),
      });
      if (res.ok) { setTitle(t); setSaved(true); setEditingTitle(false); }
    } catch { /* ignore */ }
    finally { setSaving(false); }
  };

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortBy(col); setSortDir("asc"); }
    setPage(1);
  };

  const isComplete = jobMeta?.status === "complete";
  const progress = jobMeta ? Math.round((jobMeta.processed / Math.max(jobMeta.total, 1)) * 100) : 0;
  const hasFilters = search || statusFilter !== "all" || riskFilter !== "all" || pecosFilter !== "all";

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <button onClick={onBack} className="text-slate-400 hover:text-slate-600"><ArrowLeft className="w-5 h-5" /></button>
        <div className="flex-1 min-w-0">
          {editingTitle ? (
            <div className="flex items-center gap-2">
              <input type="text" value={newTitle} onChange={e => setNewTitle(e.target.value)} placeholder="Name this screening..."
                className="text-lg font-bold text-slate-900 border-b-2 border-teal bg-transparent focus:outline-none px-0 py-0.5 w-64" autoFocus />
              <button onClick={handleSave} disabled={saving} className="text-teal hover:text-teal/80"><FloppyDisk className="w-5 h-5" weight="fill" /></button>
              <button onClick={() => setEditingTitle(false)} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <h1 className="font-display font-bold text-xl text-slate-900 truncate">{title || "Screening Results"}</h1>
              {saved && <BookmarkSimple className="w-4 h-4 text-teal shrink-0" weight="fill" />}
              <button onClick={() => { setNewTitle(title); setEditingTitle(true); }} className="text-slate-300 hover:text-slate-500" title="Rename"><PencilSimple className="w-4 h-4" /></button>
            </div>
          )}
          {!isComplete && <p className="text-xs text-slate-400">Processing...</p>}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {!saved && isComplete && (
            <Button onClick={() => { setNewTitle(title); setEditingTitle(true); }} variant="outline" size="sm" className="rounded-lg gap-1.5 text-sm">
              <BookmarkSimple className="w-4 h-4" /> Save
            </Button>
          )}
          {isComplete && (
            <>
              <Button onClick={() => onRerun(jobId)} disabled={rerunning} variant="outline" size="sm" className="rounded-lg gap-1.5 text-sm" data-testid="recheck-btn">
                <ArrowClockwise className={`w-4 h-4 ${rerunning ? "animate-spin" : ""}`} /> Re-check
              </Button>
              <a href={`${API_URL}/api/bulk-screening/download/${jobId}`} target="_blank" rel="noreferrer">
                <Button variant="outline" size="sm" className="rounded-lg gap-1.5 text-sm"><DownloadSimple className="w-4 h-4" /> Export</Button>
              </a>
            </>
          )}
        </div>
      </div>

      {/* Progress */}
      {!isComplete && (
        <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="text-slate-600">Screening...</span>
            <span className="text-slate-500">{jobMeta?.processed || 0} / {jobMeta?.total || 0}</span>
          </div>
          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full bg-teal rounded-full transition-all" style={{ width: `${progress}%` }} />
          </div>
        </div>
      )}

      {/* Summary */}
      {summary && (
        <div className={`grid grid-cols-3 ${isRerun ? "sm:grid-cols-7" : "sm:grid-cols-6"} gap-2 mb-4`}>
          <MiniStat label="Total" value={summary.total} />
          <MiniStat label="Active" value={summary.active} color="text-emerald-600" />
          <MiniStat label="Inactive" value={summary.inactive} color="text-amber-600" />
          <MiniStat label="Medicare" value={summary.pecos_enrolled} color="text-teal" />
          <MiniStat label="Flagged" value={summary.high_risk} color="text-red-600" />
          <MiniStat label="Changed" value={summary.changed_recently} color="text-violet-600" />
          {isRerun && <MiniStat label="Updated" value={summary.changed_count} color="text-amber-600" />}
        </div>
      )}

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="relative flex-1 min-w-[160px] max-w-xs">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input type="text" placeholder="Search..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal/30" />
        </div>
        <button onClick={() => setShowFilters(!showFilters)}
          className={`flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border transition-colors ${showFilters || hasFilters ? "bg-navy text-white border-navy" : "bg-white text-slate-600 border-slate-200"}`}>
          <Funnel className="w-4 h-4" /> Filters
        </button>
        <div className="ml-auto text-xs text-slate-400">{total} result{total !== 1 ? "s" : ""}</div>
      </div>

      {showFilters && (
        <div className="bg-white rounded-xl border border-slate-200 p-3 mb-3 flex flex-wrap items-end gap-3">
          <Sel label="Status" value={statusFilter} onChange={v => { setStatusFilter(v); setPage(1); }} opts={[["all","All"],["active","Active"],["inactive","Inactive"],["not found","Not Found"]]} />
          <Sel label="PECOS" value={pecosFilter} onChange={v => { setPecosFilter(v); setPage(1); }} opts={[["all","All"],["enrolled","Enrolled"],["not_enrolled","Not Enrolled"]]} />
          <Sel label="Risk" value={riskFilter} onChange={v => { setRiskFilter(v); setPage(1); }} opts={isRerun ? [["all","All"],["high","Flagged"],["clean","Clean"],["changed","Updated"],["unchanged","No Changes"]] : [["all","All"],["high","Flagged"],["clean","Clean"]]} />
          {hasFilters && <button onClick={() => { setSearch(""); setStatusFilter("all"); setPecosFilter("all"); setRiskFilter("all"); setPage(1); }} className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1"><X className="w-3 h-3" /> Clear</button>}
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden mb-3">
        {results.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-400">{isComplete ? (hasFilters ? "No results match filters" : "No results") : "Loading..."}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead className="sticky top-0 z-10">
                <tr className="bg-slate-50 border-b border-slate-200">
                  <SortTh label="NPI" col="npi" sortBy={sortBy} sortDir={sortDir} toggle={toggleSort} />
                  <SortTh label="Status" col="status" sortBy={sortBy} sortDir={sortDir} toggle={toggleSort} />
                  <SortTh label="Name" col="provider_name" sortBy={sortBy} sortDir={sortDir} toggle={toggleSort} />
                  <SortTh label="Specialty" col="primary_specialty" sortBy={sortBy} sortDir={sortDir} toggle={toggleSort} />
                  <SortTh label="State" col="state" sortBy={sortBy} sortDir={sortDir} toggle={toggleSort} />
                  <th className="text-center px-3 py-2.5 font-semibold text-slate-600">PECOS</th>
                  <SortTh label="Risk" col="risk" sortBy={sortBy} sortDir={sortDir} toggle={toggleSort} />
                  {isRerun && <th className="text-left px-3 py-2.5 font-semibold text-slate-600 whitespace-nowrap">Status Update</th>}
                  <th className="text-right px-3 py-2.5 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {results.map((r, i) => (
                  <tr key={r.npi + i} className={`border-b border-slate-100 hover:bg-slate-50/50 ${r.risk_flags?.length > 0 ? "bg-red-50/30" : ""} ${r.has_changes ? "bg-amber-50/40" : ""}`}>
                    <td className="px-3 py-2.5 font-mono text-slate-700">{r.npi}</td>
                    <td className="px-3 py-2.5"><StatusBadge s={r.status} /></td>
                    <td className="px-3 py-2.5 font-medium text-slate-900 max-w-[180px] truncate">{r.provider_name || "—"}</td>
                    <td className="px-3 py-2.5 text-slate-600 max-w-[140px] truncate">{r.primary_specialty || "—"}</td>
                    <td className="px-3 py-2.5 text-slate-500">{r.state || "—"}</td>
                    <td className="px-3 py-2.5 text-center">
                      {r.pecos_enrolled === true ? <span className="text-emerald-600 text-[11px] font-semibold">Yes</span>
                        : r.pecos_enrolled === false ? <span className="text-slate-400 text-[11px]">No</span>
                        : <span className="text-slate-300 text-[11px]">—</span>}
                    </td>
                    <td className="px-3 py-2.5">
                      {r.oig_excluded ? <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-red-100 text-red-700 text-[10px] font-bold rounded-full"><ShieldWarning className="w-3 h-3" weight="fill" />EXCLUDED</span>
                        : r.risk_flags?.length > 0 ? (
                          <div className="flex flex-wrap gap-0.5">{r.risk_flags.slice(0, 2).map((f, j) => <span key={j} className="inline-flex px-1.5 py-0.5 bg-amber-50 text-amber-700 text-[10px] font-semibold rounded-full">{f}</span>)}{r.risk_flags.length > 2 && <span className="text-[10px] text-amber-500">+{r.risk_flags.length - 2}</span>}</div>
                        ) : <span className="text-emerald-500 text-[11px] font-medium">Clean</span>}
                    </td>
                    {isRerun && (
                      <td className="px-3 py-2.5" data-testid={`status-update-${r.npi}`}>
                        <ChangeCell row={r} onReview={() => setReviewRow(r)} />
                      </td>
                    )}
                    <td className="px-3 py-2.5 text-right">
                      <Link to={`/provider/${r.npi}`} className="text-slate-400 hover:text-teal"><Eye className="w-4 h-4" /></Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm text-slate-500">
          <span>Page {page}/{totalPages}</span>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="rounded-lg h-8"><ArrowLeft className="w-3 h-3" /></Button>
            <span className="px-2 font-medium text-slate-700">{page}</span>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="rounded-lg h-8"><ArrowRight className="w-3 h-3" /></Button>
          </div>
        </div>
      )}

      {reviewRow && <ReviewChangesModal row={reviewRow} onClose={() => setReviewRow(null)} />}
    </div>
  );
}

function MiniStat({ label, value, color = "text-slate-900" }) {
  return (
    <div className="bg-white rounded-lg border border-slate-200 px-3 py-2">
      <p className={`text-lg font-bold ${color}`}>{value ?? 0}</p>
      <p className="text-[10px] text-slate-500">{label}</p>
    </div>
  );
}

function StatusBadge({ s }) {
  const m = { "Active": "bg-emerald-50 text-emerald-700", "Inactive": "bg-amber-50 text-amber-700", "Deactivated": "bg-red-50 text-red-700", "Not Found": "bg-slate-100 text-slate-500" };
  return <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold ${m[s] || "bg-slate-100 text-slate-400"}`}>{s}</span>;
}

function Sel({ label, value, onChange, opts }) {
  return (
    <div>
      <label className="block text-[10px] font-semibold text-slate-400 mb-0.5">{label}</label>
      <select value={value} onChange={e => onChange(e.target.value)} className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white">
        {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
}

function SortTh({ label, col, sortBy, sortDir, toggle }) {
  return (
    <th onClick={() => toggle(col)} className="text-left px-3 py-2.5 font-semibold text-slate-600 cursor-pointer hover:text-slate-900 select-none whitespace-nowrap">
      <span className="flex items-center gap-1">{label} {sortBy === col && (sortDir === "asc" ? <CaretUp className="w-3 h-3" /> : <CaretDown className="w-3 h-3" />)}</span>
    </th>
  );
}

function ChangeCell({ row, onReview }) {
  const changes = row.changes || [];
  if (!row.previous && changes.length === 0) {
    // New NPI that wasn't in the prior run
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-sky-50 text-sky-700 text-[11px] font-semibold rounded-full">NEW</span>;
  }
  if (changes.length === 0) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-700 text-[11px] font-semibold rounded-full">
        <CheckCircle className="w-3 h-3" weight="fill" /> No Changes
      </span>
    );
  }
  // Color severity: OIG/PECOS/NPI status changes are higher priority
  const critical = changes.some(c => ["oig_excluded", "pecos_enrolled", "status"].includes(c.field));
  const bg = critical ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-700";
  const labels = changes.slice(0, 2).map(c => c.label);
  return (
    <button
      onClick={onReview}
      className={`inline-flex items-center gap-1.5 px-2 py-1 ${bg} text-[11px] font-semibold rounded-lg hover:brightness-95 transition-all`}
      data-testid={`review-changes-${row.npi}`}
    >
      <Warning className="w-3 h-3" weight="fill" />
      {changes.length} change{changes.length > 1 ? "s" : ""}
      <span className="text-slate-500 font-normal hidden xl:inline">·</span>
      <span className="font-normal hidden xl:inline truncate max-w-[140px]">{labels.join(", ")}{changes.length > 2 ? "…" : ""}</span>
      <span className="underline underline-offset-2 font-medium">Review</span>
    </button>
  );
}

function ReviewChangesModal({ row, onClose }) {
  const changes = row.changes || [];
  const critical = changes.some(c => ["oig_excluded", "pecos_enrolled", "status"].includes(c.field));
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-200 flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h2 className="font-display font-bold text-lg text-slate-900">Review Changes</h2>
              {critical && <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-50 text-red-700 text-[10px] font-bold rounded-full"><ShieldWarning className="w-3 h-3" weight="fill" /> CRITICAL</span>}
            </div>
            <p className="text-sm text-slate-500">
              <span className="font-medium text-slate-700">{row.provider_name || "Provider"}</span>
              <span className="mx-1">·</span>
              <span className="font-mono text-xs">NPI {row.npi}</span>
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1"><X className="w-5 h-5" /></button>
        </div>

        <div className="overflow-y-auto px-6 py-4 flex-1">
          {changes.length === 0 ? (
            <div className="text-center py-10">
              <CheckCircle className="w-10 h-10 text-emerald-500 mx-auto mb-2" weight="fill" />
              <p className="text-sm text-slate-600">No changes detected since the last run.</p>
            </div>
          ) : (
            <>
              <p className="text-xs text-slate-500 mb-3 uppercase tracking-wider font-semibold">
                {changes.length} field{changes.length > 1 ? "s" : ""} changed
              </p>
              <div className="space-y-3">
                {changes.map((c, i) => (
                  <div key={i} className="border border-slate-200 rounded-xl overflow-hidden">
                    <div className="bg-slate-50 px-4 py-2 border-b border-slate-200 flex items-center gap-2">
                      <span className="font-semibold text-sm text-slate-900">{c.label}</span>
                      {["oig_excluded", "pecos_enrolled", "status"].includes(c.field) && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-red-100 text-red-700 text-[9px] font-bold rounded-full uppercase">Critical</span>
                      )}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-slate-200">
                      <div className="px-4 py-3 bg-red-50/30">
                        <p className="text-[10px] font-semibold text-red-500 uppercase tracking-wider mb-1">Previous</p>
                        <p className="text-sm text-slate-700 break-words line-through decoration-red-300/70">{c.old || "—"}</p>
                      </div>
                      <div className="px-4 py-3 bg-emerald-50/30">
                        <p className="text-[10px] font-semibold text-emerald-600 uppercase tracking-wider mb-1">Current</p>
                        <p className="text-sm text-slate-900 font-medium break-words">{c.new || "—"}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="px-6 py-3 border-t border-slate-200 flex items-center justify-between bg-slate-50">
          <Link to={`/provider/${row.npi}`} className="text-sm text-teal font-medium hover:underline flex items-center gap-1">
            <Eye className="w-4 h-4" /> Open full provider profile
          </Link>
          <Button onClick={onClose} variant="outline" size="sm" className="rounded-lg">Close</Button>
        </div>
      </div>
    </div>
  );
}

function RecheckOverlay({ done }) {
  return (
    <div className="fixed inset-0 z-50 bg-white/80 backdrop-blur-sm flex items-center justify-center">
      <div className="text-center">
        {done ? (
          <>
            <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-4" weight="fill" />
            <h2 className="font-display font-bold text-xl text-slate-900 mb-1">Re-check complete</h2>
            <p className="text-sm text-slate-500">Updated on {done}</p>
          </>
        ) : (
          <>
            <ArrowClockwise className="w-10 h-10 text-teal animate-spin mx-auto mb-4" />
            <h2 className="font-display font-bold text-xl text-slate-900 mb-1">Re-checking providers...</h2>
            <p className="text-sm text-slate-500">Running fresh checks against NPPES, PECOS, and OIG databases.</p>
          </>
        )}
      </div>
    </div>
  );
}
